import type * as A from '@/api';
import * as Sentry from '@sentry/react';
import { UserRole } from '@/api';
import { pluck, difference } from 'underscore';
import User from './user';
import Organization from './organization';
import WorkspaceLite from './workspaceLite';
import Source from './source';
import Folder from './folder';
import { graphql } from '@/utils/graphql';
import { addBackendSuffix, isDevBackend } from '@/utils/namingConvention';
import { assertIsDefined } from '@/utils/typeChecks';
import { logger } from '@/utils/logger';

// default role for backward compatibility, and to avoid ADMIN role in the role field of WorkspaceUser by mistake
function toDefaultRole(role: UserRole | undefined): UserRole {
  if (role === UserRole.ADMIN) return UserRole.EDITOR;
  return role ?? UserRole.EDITOR;
}

const ns = 'app.services.workspace';

type WorkspaceConstructorInput = A.GetWorkspaceQuery['getWorkspace'];

type AvatarField = 'avatarColor' | 'avatarIcon' | 'avatarEmoji' | 'avatarInitials';

// The avatar fields are optional strings in the schema, and null clears one.
type WorkspaceUpdateInput = Partial<Omit<WorkspaceConstructorInput, AvatarField>> &
  Partial<Record<AvatarField, string | null>>;

export type WorkspaceUserFieldType = { user: User; role: UserRole };

export default class Workspace {
  readonly id: string;

  readonly name: string;

  readonly seq: number;

  readonly organization: Organization;

  /**
   * The AI dashboard this workspace's Home page renders, or null when Home is not set up. Not
   * readonly: `update()` writes changed fields back onto the instance.
   */
  homeDashboardId: string | null;

  /**
   * The avatar, as set from the sidebar header's Customize dialog. Each null when unset: see
   * `src/components/Avatar/workspaceAvatar.ts` for what is drawn then. Not readonly, like
   * `homeDashboardId`: `update()` writes them back.
   */
  avatarColor: string | null;

  avatarIcon: string | null;

  avatarEmoji: string | null;

  avatarInitials: string | null;

  #isUsersLoaded: boolean;

  #users: WorkspaceUserFieldType[];

  get users(): WorkspaceUserFieldType[] {
    if (this.#isUsersLoaded) return this.#users;
    throw new Error('Unexpected - Workspace - get users() - !#isUsersLoaded');
  }

  private constructor(input: WorkspaceConstructorInput, orgAdminIds?: string[]) {
    this.id = input.id;
    this.name = input.name;
    this.seq = input.seq;
    this.organization = new Organization(input.organization);
    this.homeDashboardId = input.homeDashboardId ?? null;
    this.avatarColor = input.avatarColor ?? null;
    this.avatarIcon = input.avatarIcon ?? null;
    this.avatarEmoji = input.avatarEmoji ?? null;
    this.avatarInitials = input.avatarInitials ?? null;
    if (input.users?.items) {
      this.#isUsersLoaded = true;
      assertIsDefined(orgAdminIds, 'BEEM250804163925');
      this.#users =
        input.users?.items.map((el) => ({
          user: new User(el.user),
          role: orgAdminIds.includes(el.user.id) ? UserRole.ADMIN : toDefaultRole(el.role),
        })) || [];
    } else {
      this.#isUsersLoaded = false;
      this.#users = [];
    }

    this.getSources = this.getSources.bind(this);
    this.update = this.update.bind(this);
    this.addMembers = this.addMembers.bind(this);
    this.removeMembers = this.removeMembers.bind(this);
    this.getNonAdminMemberRole = this.getNonAdminMemberRole.bind(this);
    this.updateMemberRole = this.updateMemberRole.bind(this);
  }

  async getSources(): Promise<Source[]> {
    return Source.getSourcesOwnByWorkspace(this.id);
  }

  async update(input: WorkspaceUpdateInput): Promise<void> {
    logger.info({ label: 'app.services.workspace.update.input', message: input });
    // amplify codegen types optional input fields as `?: string`, but AppSync takes null for
    // them, and null is what clears a field. The service's own type says so; this is the one place
    // that bridges the two.
    await graphql('updateWorkspace', {
      input: { id: this.id, ...input } as A.UpdateWorkspaceInput,
    });
    Object.entries(input).forEach(([k, v]) => {
      // @ts-expect-error this is ok because there's no harm in setting a value for a prop if it doesn't exist, maybe in the future TS will handle this better
      this[k] = v;
    });
  }

  private async refreshWarehousePermissionForMember(userId: string): Promise<void> {
    const user = await User.getUserById(userId);
    const { hasWarehouseCredentials } = await user.hasWarehouseCredentials(this.organization);
    if (!hasWarehouseCredentials) return;
    const password = await user.createOrUpdateWarehouseCredentials(this.organization);
    if (password !== '') throw new Error('BEEM220711104055');
  }

  async addMembers(memberIds: string[], roles: Record<string, UserRole> = {}): Promise<void> {
    logger.info({ label: 'app.services.workspace.addMembers.memberIds', message: memberIds });
    const organizationId = this.organization.id;
    const workspaceId = this.id;
    const currentUserIds = pluck(this.users, 'id');
    const userIds = difference(memberIds, currentUserIds);
    await Promise.all(
      userIds.map(async (userId): Promise<void> => {
        await graphql('createWorkspaceUser', {
          input: {
            id: `${workspaceId}#${userId}`,
            organizationId,
            workspaceId,
            userId,
            role: toDefaultRole(roles[userId]),
          },
        });
        if (roles[userId] === UserRole.ADMIN) {
          const { items } = await graphql('listOrganizationUsersByUser', {
            userId,
            organizationId: { eq: organizationId },
          });
          if (items.length !== 1) throw new Error('BEEM240217093042');
          await graphql('updateOrganizationUser', { input: { id: items[0].id, isOrgAdmin: true } });
        }
        await this.refreshWarehousePermissionForMember(userId);
      }),
    );
  }

  async removeMembers(memberIds: string[]): Promise<void> {
    logger.info({ label: 'app.services.workspace.removeMembers.memberIds', message: memberIds });
    const workspaceId = this.id;
    await Promise.all(
      memberIds.map(async (userId): Promise<void> => {
        const data = await graphql('listWorkspaceUsersByUser', {
          userId,
          workspaceId: { eq: workspaceId },
        });
        // Idempotent: the WorkspaceUser link may already be gone if another
        // admin removed this member concurrently. Treat 0 matches as a no-op
        // (still refresh warehouse permission to converge to the desired state)
        // instead of failing. refreshWarehousePermissionForMember is idempotent
        // on the warehouse side — revoking already-revoked permissions is a
        // no-op, so a concurrent-removal race won't cause spurious errors.
        // More than 1 match is a real data-integrity issue.
        if (data.items.length > 1)
          throw new Error('Unexpected - workspace - removeMembers - data.items.length > 1');
        if (data.items.length === 0) {
          logger.info({
            label: 'app.services.workspace.removeMembers.alreadyRemoved',
            message: userId,
          });
          await this.refreshWarehousePermissionForMember(userId);
          return;
        }
        await graphql('deleteWorkspaceUser', { input: { id: data.items[0].id } });
        await this.refreshWarehousePermissionForMember(userId);
      }),
    );
  }

  async getNonAdminMemberRole(memberId: string): Promise<UserRole> {
    logger.info({ label: `${ns}.getNonAdminMemberRole.memberId`, message: memberId });
    const workspaceId = this.id;
    const data = await graphql('listWorkspaceUsersByUser', {
      userId: memberId,
      workspaceId: { eq: workspaceId },
    });
    if (data.items.length !== 1) throw new Error('BEEM250730144711');
    return toDefaultRole(data.items[0].role);
  }

  async updateMemberRole(memberId: string, role: UserRole): Promise<void> {
    logger.info({ label: `${ns}.updateMemberRole.memberId`, message: memberId });
    logger.info({ label: `${ns}.updateMemberRole.role`, message: role });
    const workspaceId = this.id;
    const organizationId = this.organization.id;

    if (role !== UserRole.ADMIN) {
      const data = await graphql('listWorkspaceUsersByUser', {
        userId: memberId,
        workspaceId: { eq: workspaceId },
      });
      if (data.items.length !== 1) throw new Error('BEEM250730144710');
      await graphql('updateWorkspaceUser', { input: { id: data.items[0].id, role } });
    }

    const { items } = await graphql('listOrganizationUsersByUser', {
      userId: memberId,
      organizationId: { eq: organizationId },
    });
    if (items.length !== 1) throw new Error('BEEM240217093043');
    await graphql('updateOrganizationUser', {
      input: { id: items[0].id, isOrgAdmin: role === UserRole.ADMIN },
    });
  }

  static async getWorkspaceById(id: string): Promise<Workspace> {
    logger.info(id, { label: 'app.services.workspace.getWorkspaceById.id' });
    const data = await graphql('getWorkspace', { id });
    const { items } = await graphql('listOrganizationUsersByOrganization', {
      organizationId: data.organization.id,
      filter: { isOrgAdmin: { eq: true } },
    });
    const orgAdminIds = items.map((el) => el.userId);
    return new Workspace(data, orgAdminIds);
  }

  static async getWorkspacesByUser(userId: string): Promise<Workspace[]> {
    logger.info(userId, { label: 'app.services.workspace.getWorkspacesByUser.userId' });
    const data = await graphql('listWorkspaceUsersByUser', { userId });
    // An orphaned WorkspaceUser can reference a workspace whose organization the
    // user is not authorized to read. AppSync nulls the offending nested field
    // (see partial-success handling in utils/graphql). Skip those memberships
    // instead of crashing the Workspace/Organization constructors, which would
    // blank the whole app on global load.
    // Guard the construction itself rather than probing for one specific nulled
    // field: `organization` is what an unreadable org nulls today, but
    // `workspace.id`/`name`/`seq` are non-null in the schema too, so any of them
    // could arrive null and throw. Catching here keeps one bad membership from
    // blanking the whole app on global load.
    const skipped: { id: string; reason: string }[] = [];
    const workspaces: Workspace[] = [];
    data.items.forEach((el) => {
      try {
        workspaces.push(new Workspace(el.workspace));
      } catch (e) {
        const reason = e instanceof Error ? e.message : String(e);
        skipped.push({ id: String(el?.id), reason });
        logger.warn({
          label: `${ns}.getWorkspacesByUser.skippedUnreadableMembership`,
          message: `${el?.id}: ${reason}`,
        });
      }
    });

    // Skipping keeps the user working, which also means nobody would ever notice
    // the broken data. Report it so the orphaned rows get reconciled (BEEM-2279).
    // The reason travels with it: this catch is broad enough to swallow a real
    // constructor bug, so the message has to be visible rather than inferred.
    if (skipped.length > 0) {
      Sentry.captureMessage(
        `${ns}.getWorkspacesByUser.skippedUnreadableMembership: ${skipped
          .map((sk) => sk.id)
          .join(', ')}`,
        {
          level: 'warning',
          extra: { userId, skipped, totalItems: data.items.length },
        },
      );
    }

    return workspaces;
  }

  static async getWorkspacesByOrganization(organizationId: string) {
    logger.info({
      label: 'app.services.workspace.getWorkspacesByOrganization.organizationId',
      message: organizationId,
    });
    const data = await graphql('listWorkspacesByOrganization', { organizationId });
    return data.items.map((el) => new Workspace(el));
  }

  static async getWorkspacesWhichSharedDatasetWithThisWorkspace(
    thisWorkspaceId: string,
  ): Promise<WorkspaceLite[]> {
    const data = await graphql('listDatasetShareFromWorkspaceWithWorkspaceLinksByWithWorkspaceId', {
      withWorkspaceId: thisWorkspaceId,
    });
    return data.items.map((el) => new WorkspaceLite(el.fromWorkspace));
  }

  static async create(
    variables: A.CreateWorkspaceMutationVariables,
    { userIds, roles }: { userIds: string[]; roles?: Record<string, UserRole> },
    _unitTestOnly_DoNotCreateRootFolder = false,
  ): Promise<string> {
    logger.info({ label: 'app.services.workspace.create.variables', message: variables });
    logger.info({ label: 'app.services.workspace.create.userIds', message: userIds });

    // IMPORTANT: must check workspace limit on organization
    const organizationId = variables.input?.organizationId;
    assertIsDefined(organizationId, 'BEEM211105120247');
    const organization = await Organization.getOrganizationById(organizationId);
    const workspaces = await Workspace.getWorkspacesByOrganization(organizationId);
    if (organization.workspaceLimit <= workspaces.length)
      throw new Error(
        `WORKSPACE_LIMIT_REACHED - You've reached the workspace limit for this organization. Please contact BEEM to increase the limit.`,
      );
    // WORKSPACE_SEQ_OFFSET exists purely so parallel test runners derive disjoint Redshift
    // schema names (ws<seq>@<backendId>_*). It must never take effect against production:
    // a stray env var there would offset real customers' schema numbering. Gate it on the
    // app's own production predicate rather than on the env var being absent.
    const workspaceSeqOffset = isDevBackend()
      ? parseInt(process.env.WORKSPACE_SEQ_OFFSET || '0', 10) || 0
      : 0;
    const workspaceSeq = workspaces.length + 1 + workspaceSeqOffset;

    // now can create the workspace
    const temp = { ...variables };
    if (temp.input) temp.input.seq = workspaceSeq;
    const data = await graphql('createWorkspace', temp);
    const workspaceId = data.id;

    if (!_unitTestOnly_DoNotCreateRootFolder)
      Folder.create({ name: 'Warehouse', parentId: workspaceId });

    // call beem agent to create the redshift group, user, schema, etc..
    await graphql('configureNewWorkspaceInTheWarehouse', {
      query: {
        orgId: organization.cloudResourceId,
        workspaceSeq: addBackendSuffix(String(workspaceSeq)),
      },
    });

    // add the initial users to this workspace
    const ws = await Workspace.getWorkspaceById(workspaceId);
    await ws.addMembers(userIds, roles);

    return workspaceId;
  }
}
